import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatError } from "../../lib/api";
import { Shell } from "./_shell";
import { EmailDot, StatusSelect, ConfirmDialog } from "./_shared";

export default function Inquiries() {
  const [list, setList] = useState([]);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/inquiries").then(r => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => {
    try { await api.put(`/inquiries/${id}/status`, { status }); toast.success(`Marked ${status}`); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const doDelete = async (q) => {
    setBusy(true);
    try { await api.delete(`/inquiries/${q.id}`); toast.success(`Message #${q.inquiry_no ?? ""} deleted`); setConfirm(null); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Shell active="inquiries">
      <div className="p-5 md:p-10">
        <div className="overline mb-2">Contact form</div>
        <h1 className="font-display text-4xl md:text-6xl uppercase leading-none mb-8">Contact messages.</h1>
        <div className="space-y-3" data-testid="inquiries-list">
          {list.map(q => (
            <div key={q.id} className={`border p-5 bg-paper ${["completed","archived","spam"].includes(q.admin_status) ? "border-black/20 opacity-70" : "border-ink"}`} data-testid={`admin-inquiry-${q.id}`}>
              <div className="flex justify-between flex-wrap gap-2 mb-2">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display text-xl leading-none" data-testid={`inquiry-no-${q.id}`}>#{q.inquiry_no ?? "—"}</span>
                    <span className="font-bold">{q.name}</span>
                    <span className="text-xs text-black/60">{q.email}</span>
                  </div>
                  <div className="text-xs text-black/60 mt-1">{q.business} · {q.phone}</div>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex items-center gap-3">
                    <EmailDot sent={q.email_customer_sent} label="Customer" />
                    <EmailDot sent={q.email_business_sent} label="Business" />
                  </div>
                  <StatusSelect value={q.admin_status} onChange={(s) => setStatus(q.id, s)} testid={`inquiry-status-${q.id}`} />
                  <div className="text-xs text-black/60 w-full md:w-auto">{new Date(q.created_at).toLocaleString()}</div>
                </div>
              </div>
              <p className="text-sm mt-2 whitespace-pre-wrap">{q.message}</p>
              <div className="mt-4 pt-3 border-t border-black/10 flex flex-wrap gap-2">
                <button onClick={() => setStatus(q.id, "archived")} className="btn-ghost text-[11px] px-3 py-1.5" data-testid={`inquiry-archive-${q.id}`}>
                  Archive
                </button>
                <button onClick={() => setConfirm(q)} className="text-[11px] px-3 py-1.5 underline text-soda" data-testid={`inquiry-delete-${q.id}`}>
                  Delete
                </button>
              </div>
            </div>
          ))}
          {list.length === 0 && <div className="border border-ink p-8 text-center text-black/50">No messages yet.</div>}
        </div>
      </div>
      <ConfirmDialog
        open={!!confirm}
        title={`Delete message #${confirm?.inquiry_no ?? ""}?`}
        body={`This will permanently remove the contact message from ${confirm?.name || "this contact"}. This action cannot be undone.\n\nUse "Archive" instead if you want to keep it for records.`}
        confirmLabel="Delete permanently" danger busy={busy}
        onConfirm={() => doDelete(confirm)} onCancel={() => setConfirm(null)}
        testid="inquiry-delete-dialog"
      />
    </Shell>
  );
}
